import { PlusIcon } from '@persona-id/design-tokens/icons';

import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import FormCreateAccountType from 'dashboard/forms/FormCreateAccountType/FormCreateAccountType';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { AccountType } from 'dashboard/models/account-type';
import AccountTypesTable from 'dashboard/views/DashboardAccountTypesView/AccountTypesTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip/Tooltip';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';
import { permissionErrorString } from 'lib/permissions-errors';

interface FormCreateAccountTypeData {
  name: string;
}

export type DashboardAccountTypesViewProps = {
  accountTypes: AccountType[];
  createAccountType: (args: FormCreateAccountTypeData) => Promise<void>;
  isLoadingAccountTypes: boolean;
};

const DashboardAccountTypesView = (props: DashboardAccountTypesViewProps) => {
  const { accountTypes, createAccountType, isLoadingAccountTypes } = props;
  const [
    createTransactionTypeModal,
    { enable: showCreateAccountTypeModal, disable: hideCreateAccountTypeModal },
  ] = useToggle(false);
  const handleCreateAccountType = async (args: FormCreateAccountTypeData) => {
    await createAccountType(args);
    hideCreateAccountTypeModal();
  };

  const hasAccountTypeCreatePermission = usePermission(Permission.AccountTypeCreate);

  return (
    <Container>
      <ModalWrapper
        isOpen={createTransactionTypeModal}
        handleCloseModal={hideCreateAccountTypeModal}
      >
        <ModalContainer>
          <ModalHeader onClose={hideCreateAccountTypeModal}>Create Account Type</ModalHeader>
          <ModalBody>
            <FormCreateAccountType
              handleClose={hideCreateAccountTypeModal}
              onSubmit={handleCreateAccountType}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <InactiveEnvironmentBanner />
      <Header data-test='account-types__header' title='Account types'>
        <Tooltip
          disabled={hasAccountTypeCreatePermission}
          side='bottom'
          trigger={
            <Button
              aria-label='Add new type'
              data-test='account-types__create-type-button'
              variant='basic'
              disabled={!hasAccountTypeCreatePermission}
              onClick={showCreateAccountTypeModal}
              prefixIcon={<PlusIcon />}
            >
              Add type
            </Button>
          }
        >
          <Paragraph>{permissionErrorString(Permission.AccountCreate)}</Paragraph>
        </Tooltip>
      </Header>
      <Section>
        <Card>
          <Card.Body>
            <Card.Title>All Account Types</Card.Title>
            <AccountTypesTable accountTypes={accountTypes} loading={isLoadingAccountTypes} />
          </Card.Body>
        </Card>
      </Section>
    </Container>
  );
};

export default DashboardAccountTypesView;
